<script>
	import FileUpload from '@components/FileUpload.vue';
	import { mapActions, mapGetters } from 'vuex';
	import ImageCropper from '@components/ImageCropper.vue';

	export default {
		name: 'NavCompanyLogo',
		components: { ImageCropper, FileUpload },
		props: {
			logoUrl: String
		},
		data() {
			return {
				isUploaderOpen: false,
				isUploading: false,
				droppedFile: null
			};
		},
		computed: {
			...mapGetters({
				loggedInEmployee: 'loggedInEmployee'
			}),
			imgSrc() {
				return this.logoUrl || require('@/assets/yhrs_logo.png');
			},
			imgAlt() {
				return this.logoUrl ? 'Your Company Logo' : 'Your HR System Logo';
			},
			imgTooltip() {
				return this.logoUrl ? 'Click to change your logo' : 'Click to upload your logo';
			},
			uploadLogoTitle() {
				return this.logoUrl ? 'Edit Company Logo' : 'Upload Your Logo'
			}
		},
		methods: {
			...mapActions({
				uploadLogo: 'uploadLogo'
			}),
			handleLogoClick() {
				if (this.loggedInEmployee && this.loggedInEmployee.isAdmin)
					this.isUploaderOpen = true;
			},
			async handleSubmitLogo(formData) {
				this.isUploading = true;

				const file = formData.get('file');

				const result = await this.uploadLogo(file);

				if (result.isSuccess) {
					this.isUploaderOpen = false;
				}

				this.isUploading = false;
			},
			handleCancelUpload() {
				this.isUploaderOpen = false;
			},
			handleLogoCrop(file) {
				this.$refs.fileUpload.setFiles(file);
			},
			handleCropCancel(previousFile) {
				this.$refs.fileUpload.setFiles(previousFile);
			}
		}
	};
</script>

<template>
	<div>
		<div v-if="!logoUrl" class="pa-3">
			<div class="logo-placeholder" @click="handleLogoClick"></div>
		</div>
		<div v-else-if="loggedInEmployee.isAdmin">
			<div class="nav-company-logo edit" @click="handleLogoClick">
				<div class="overlay"></div>
				<img :src="imgSrc" :alt="imgAlt" style="max-height: 125px;" />
				<v-icon class="edit-icon">mdi-pencil</v-icon>
			</div>
		</div>
		<div v-else>
			<div class="nav-company-logo">
				<img :src="imgSrc" :alt="imgAlt" style="max-height: 125px;" />
			</div>
		</div>

		<file-upload
				ref="fileUpload"
				dialog-id="logoUpload"
				:title="uploadLogoTitle"
				:dialog="isUploaderOpen"
				accept="image/*"
				primary-action="Upload"
				secondary-action="Cancel"
				hide-file-accept
				:loading="isUploading"
				@droppedFile="droppedFile = $event"
				@onPrimaryAction="handleSubmitLogo"
				@onSecondaryAction="handleCancelUpload"
			/>

			<image-cropper
				input-selector="#logoUpload input"
				preview-selector="#logoUpload .image-preview img"
				:croppie-config="{
					viewport: {
						width: 300,
						height: 125
					}
				}"
				:droppedFile="Array.isArray(droppedFile) && droppedFile.length > 0 ? droppedFile[0] : droppedFile"
				@cropped="handleLogoCrop"
				@cancel="handleCropCancel"
			/>
	</div>
</template>

<style scoped lang="scss">
	.nav-company-logo {
	position: relative;
	padding: 0;
	transition: 0.2s all ease-in-out;
	border-radius: 5px;
	display: flex;
	align-items: center;
	justify-content: center;
	overflow: hidden;

	&.edit {
		cursor: pointer;

		.overlay {
			position: absolute;
			top: 0;
			left: 0;
			width: 100%;
			height: 100%;
			background: rgba(0, 0, 0, 0); /* Transparent by default */
			transition: background 0.2s ease-in-out;
		}

		&:hover .overlay {
			background: rgba(0, 0, 0, 0.1); /* Grey overlay on hover */
		}

		.edit-icon {
			position: absolute;
			bottom: 10px;
			right: 10px;
			background: rgba(0, 0, 0, 0.7);
			color: #fff;
			border-radius: 50%;
			width: 28px;
			height: 28px;
			display: flex;
			align-items: center;
			justify-content: center;
			font-size: 18px;
			opacity: 0;
			transition: opacity 0.2s ease-in-out;
			z-index: 2;
		}

		&:hover .edit-icon {
			opacity: 1;
		}
	}

	img {
		max-width: 100%;
		height: auto;
		max-height: 125px;
	}
}

	.logo-placeholder {
		width: 100%;
		height: 100px;
		display: flex;
		align-items: center;
		justify-content: center;
		border: 2px dashed #ccc;
		border-radius: 8px;
		color: #aaa;
		font-size: 14px;
		font-weight: bold;
		cursor: pointer;
		transition: background-color 0.3s ease, color 0.3s ease;
	}

	.logo-placeholder:hover {
		background-color: #f0f0f0;
		color: #888;
	}

	.logo-placeholder::before {
		content: 'Add Your Company Logo';
		text-align: center;
	}
</style>
